import { useState, useEffect } from 'react';
import { clientEntitlementsService } from '@services/client-entitlements.service.ts';
import { useAuth } from '@/hooks/useAuth';

interface UseClientPlanResult {
  featureAccess: Record<string, boolean>;
  loading: boolean;
  hasFeature: (feature: string) => boolean;
}

/**
 * Feature access rarely changes mid-session, so it's fetched once per login and
 * reused by every useClientPlan() call site (Sidebar, future feature-gated pages)
 * instead of refetching on every mount. clearClientFeatureCache() must be called
 * on every auth transition (login/logout/checkAuth) — see store/auth/authSlice.ts —
 * otherwise a second user logging in within the same SPA session would see the
 * previous user's cached access.
 */
let cachedFeatureAccess: Promise<Record<string, boolean>> | null = null;

function fetchMyFeatures(): Promise<Record<string, boolean>> {
  if (!cachedFeatureAccess) {
    cachedFeatureAccess = clientEntitlementsService.getMyFeatures().catch((e) => {
      cachedFeatureAccess = null;
      throw e;
    });
  }
  return cachedFeatureAccess;
}

export function clearClientFeatureCache(): void {
  cachedFeatureAccess = null;
}

/**
 * Client-role counterpart of useExpertPlan — feature set only, nothing about
 * subscription/billing status (that's a separate concern, out of scope here).
 */
export const useClientPlan = (): UseClientPlanResult => {
  const { user } = useAuth();
  const isClient = (user?.roles || []).some((r: string) => r.toLowerCase() === 'client');

  const [featureAccess, setFeatureAccess] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(isClient);

  useEffect(() => {
    if (!isClient) return;

    setLoading(true);
    fetchMyFeatures()
      .then(setFeatureAccess)
      .catch(() => setFeatureAccess({}))
      .finally(() => setLoading(false));
  }, [isClient]);

  const hasFeature = (feature: string): boolean => {
    if (!isClient) return true;
    return featureAccess[feature] === true;
  };

  return { featureAccess, loading: isClient ? loading : false, hasFeature };
};
